import localforage from 'localforage';
import { getFirestore} from 'firebase/firestore';
import { db } from '../firebase';
import { getDoc, doc, collection, getDocs, query, where, collectionGroup } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';

const PRACTICES_STORE = 'practices';
const LAST_UPDATE_KEY = 'lastPracticeUpdate';
const METADATA_KEY = 'practices_metadata';
const VERSION_CHECK_INTERVAL = 60 * 60 * 1000; // Check for new version every hour
// Check for updates every day for admin users, weekly for regular users
const ADMIN_UPDATE_INTERVAL = 24 * 60 * 60 * 1000; // 1 day
const REGULAR_UPDATE_INTERVAL = 7 * 24 * 60 * 60 * 1000; // 1 week
// Configure which data source to use when running locally
const USE_FIREBASE_STORAGE = true; // Set to false to always use the local file during development
const FORCE_ONLINE_FOR_ADMIN = true; // Always use online version for admin pages

// Add cache busting timestamp to avoid caching issues
async function fetchPracticesJson() {
  try {
    const timestamp = new Date().getTime();
    const response = await fetch(`/practices.json?t=${timestamp}`);
    if (!response.ok) {
      throw new Error('Failed to fetch practices.json');
    }
    return response.json();
  } catch (error) {
    console.error('Error fetching practices.json:', error);
    return [];
  }
}

/**
 * Utility for managing practice data with separate caches for full practices and user selections
 */
class PracticeDataUtil {
  constructor() {
    // CRITICAL FIX: Use two separate cache objects to prevent contamination
    this.practiceCache = {
      allPractices: [], // For the complete dataset (~9500 practices)
      userSelections: {}, // For user's selected practices (per user ID)
      timestamp: null,
      loadPromise: null
    };
    this.initialized = false;
    this.syncService = null;
    
    // Add properties from the second implementation
    this.practices = null;
    this.lastInitTime = null;
    this.lastVersionCheck = 0;
    this.practiceVersions = new Map();
    
    // Add cache related properties
    this.cacheDuration = 24 * 60 * 60 * 1000; // 1 day in ms
    this.initPromise = null;
    this.pendingRefresh = null;

    // Add new caching properties
    this.pendingUpdatesCache = null;
    this.pendingUpdatesCacheTime = null;
    this.pendingUpdatesCacheDuration = 5 * 60 * 1000; // 5 minutes

    // Add enhanced caching properties
    this.firebaseUpdatesCache = null;
    this.firebaseUpdatesCacheTime = null;
    this.firebaseUpdatesTTL = 5 * 60 * 1000; // 5 minutes
    this.initializationPromise = null;

    // Enhanced Firebase updates cache
    this.firebaseUpdates = {
      data: null,
      timestamp: null,
      promise: null,
      TTL: 5 * 60 * 1000 // 5 minutes
    };

    // Add enhancement cache
    this.enhancedPracticesCache = {
      data: new Map(),
      timestamp: null,
      TTL: 5 * 60 * 1000, // 5 minutes
      pendingPromises: new Map() // Add promise cache
    };

    // Add initialization tracking
    this.initState = {
      loading: false,
      initialized: false,
      timestamp: null,
      promise: null
    };

    // Add initialization status tracker
    this._initStatus = {
      initialized: false,
      loading: false,
      timestamp: null,
      promise: null
    };

    // Add a global initialization lock
    this._globalInitLock = {
      promise: null,
      timestamp: null,
      TTL: 5 * 60 * 1000 // 5 minutes
    };

    // Add a practice loading lock
    this._practiceLoadLock = {
      promises: new Map(),
      timestamp: null,
      TTL: 5 * 60 * 1000
    };

    // Private state
    this._initialized = false;
    this._initializing = false;
    this._initializePromise = null;
    this._lastUpdateTimestamp = Date.now();  // Add this line
    this._practiceMap = new Map();
    this._lastInitialized = null;
    
    // Caching
    this._enhancedPracticeCache = new Map();
    this._pendingUpdates = null;
    this._lastUpdateCheck = null;
    
    // Debug mode
    this.debug = process.env.NODE_ENV === 'development';

    // Add an update timestamp that components can check
    this._lastUpdateTimestamp = Date.now();
  }

  isInitialized() {
    console.log('[PRACTICE-DEBUG] isInitialized called');
    return this._initStatus.initialized && 
           this._initStatus.timestamp && 
           (Date.now() - this._initStatus.timestamp < this.cacheDuration);
  }

  // Add proper initialization method
  async initializePracticeData(forceRefresh = false) {
    console.log('[PRACTICE-DEBUG] initializePracticeData called with forceRefresh:', forceRefresh);
    const now = Date.now();

    // Return existing global initialization if in progress
    if (this._globalInitLock.promise && !forceRefresh) {
      console.log('[PRACTICE-DEBUG] Using existing global initialization');
      return this._globalInitLock.promise;
    }

    // Return cached result if valid
    if (!forceRefresh && 
        this._globalInitLock.timestamp && 
        now - this._globalInitLock.timestamp < this._globalInitLock.TTL) {
      console.log('[PRACTICE-DEBUG] Using cached global initialization');
      return this.practices;
    }

    // Create new initialization promise
    console.log('[PRACTICE-DEBUG] Starting new global initialization');
    this._globalInitLock.promise = this._doInitialize(forceRefresh);

    try {
      const result = await this._globalInitLock.promise;
      this._globalInitLock.timestamp = now;
      return result;
    } finally {
      this._globalInitLock.promise = null;
    }
  }

  // Improve _doInitialize with better caching
  async _doInitialize(forceRefresh) {
    console.log('[PRACTICE-DEBUG] _doInitialize called with forceRefresh:', forceRefresh);
    try {
      if (this.practices && this.lastInitTime && !forceRefresh) {
        const now = Date.now();
        if (now - this.lastInitTime < this.cacheDuration) {
          console.log('[PRACTICE-DEBUG] Using cached practice data');
          return this.practices;
        }
      }

      console.log('[PRACTICE-DEBUG] Loading fresh practice data');
      const data = await this._fetchPracticesData(forceRefresh);
      this.practices = data;
      this.lastInitTime = Date.now();
      this.initialized = true;
      return this.practices;
    } catch (error) {
      console.error('[PRACTICE-DEBUG] Error in _doInitialize:', error);
      throw error;
    }
  }

  /**
   * CRITICAL FIX: Replace the syncService initialization with a direct Firestore approach
   * This removes the circular dependency
   */
  async getPendingUpdates(forceRefresh = false) {
    // Use cached updates if available and not forcing refresh
    if (this._pendingUpdates && !forceRefresh) {
      this._log('Using cached updates');
      return this._pendingUpdates;
    }
    
    try {
      this._log('🔄 Getting updates from admin/practiceUpdates document');
      
      // ADD: Check if user has admin permissions before attempting to read admin documents
      const auth = getAuth();
      const currentUser = auth.currentUser;
      
      if (!currentUser) {
        console.log('[PRACTICE-DEBUG] No authenticated user, skipping admin updates check');
        return {};
      }
      
      // ADD: Only attempt to read admin documents if user email suggests admin access
      const adminEmails = process.env.REACT_APP_ADMIN_EMAILS?.split(',').map(email => email.trim()) || [];
      const isAdmin = adminEmails.includes(currentUser.email);
      
      if (!isAdmin) {
        console.log('[PRACTICE-DEBUG] Non-admin user, skipping admin updates check');
        this._pendingUpdates = {};
        this._updatesAvailable = false;
        return {};
      }
      
      const practiceUpdatesDocRef = doc(db, 'admin', 'practiceUpdates');
      const practiceUpdatesDoc = await getDoc(practiceUpdatesDocRef);
      
      const updates = {};
      
      if (practiceUpdatesDoc.exists()) {
        this._log('✅ Found practice updates document');
        
        // Get the updatedPractice field which contains the map of updates
        const data = practiceUpdatesDoc.data();
        
        if (data && data.updatedPractice) {
          this._log('✅ Found updatedPractice map with practice updates');
          
          // Extract practice code from the updatedPractice map
          const pracCode = data.updatedPractice.prac_code;
          
          if (pracCode) {
            // Store the entire updatedPractice object under the practice code key
            updates[pracCode] = data.updatedPractice;
            
            this._log(`✅ Found update for practice: ${pracCode} (${data.updatedPractice.prac_name})`);
            
            // Clear in-memory cache for this practice
            this._enhancedPracticeCache.delete(pracCode);
            
            // If practice exists in map, update it directly
            if (this._practiceMap.has(pracCode)) {
              const existingPractice = this._practiceMap.get(pracCode);
              const updatedPractice = {...existingPractice, ...data.updatedPractice};
              this._practiceMap.set(pracCode, updatedPractice);
              this._log(`✅ Directly updated practice ${pracCode} in memory cache`);
              
              // Set update timestamp to notify components data has changed
              this._lastUpdateTimestamp = Date.now();
              this._log(`✅ Updated timestamp to ${this._lastUpdateTimestamp}`);
              
              // CRITICAL FIX: Update the localStorage cache with the updated practice data
              try {
                // Get the current localStorage cache
                this._log('🔄 Attempting to update localStorage practice data...');
                const cachedData = localStorage.getItem('practices_data');
                
                if (cachedData) {
                  this._log('✅ Found existing practices_data in localStorage');
                  
                  try {
                    // Parse the localStorage data
                    const practices = JSON.parse(cachedData);
                    
                    // CRITICAL FIX: Handle object format (not array)
                    if (typeof practices === 'object' && !Array.isArray(practices)) {
                      this._log(`📊 localStorage has object format with keys: ${Object.keys(practices).length}`);
                      
                      // Directly update the practice using its key
                      if (practices[pracCode]) {
                        this._log(`✅ Found practice ${pracCode} in localStorage object`);
                        
                        // Log before state
                        this._log('📝 BEFORE update:', JSON.stringify(practices[pracCode]).substring(0, 100) + '...');
                        
                        // Update the practice with new data
                        practices[pracCode] = {
                          ...practices[pracCode],
                          ...data.updatedPractice
                        };
                        
                        // Log after state
                        this._log('📝 AFTER update:', JSON.stringify(practices[pracCode]).substring(0, 100) + '...');
                        
                        // Save the updated data back to localStorage
                        this._log('💾 Saving updated practices to localStorage...');
                        localStorage.setItem('practices_data', JSON.stringify(practices));
                        localStorage.setItem('practices_timestamp', Date.now().toString());
                        this._log(`✅ Successfully updated practice ${pracCode} in localStorage`);
                      } else {
                        this._log(`❌ Practice ${pracCode} not found in localStorage object`);
                      }
                    } else {
                      this._log('❌ localStorage data is not an object:', typeof practices);
                    }
                  } catch (parseError) {
                    this._error('❌ Error parsing localStorage data:', parseError);
                  }
                } else {
                  this._log('❌ No practices_data found in localStorage');
                }
              } catch (storageError) {
                this._error('❌ Failed to update localStorage cache:', storageError);
              }
            } else {
              this._log(`❌ Practice ${pracCode} not found in in-memory practice map`);
            }
          }
        } else {
          this._log('ℹ️ No updatedPractice map found in the document');
        }
      } else {
        this._log('ℹ️ admin/practiceUpdates document not found');
      }
      
      this._pendingUpdates = updates;
      this._updatesAvailable = Object.keys(updates).length > 0;
      
      return updates;
    } catch (error) {
      this._error('❌ Error getting practice updates:', error);
      // ADD: Don't throw the error, just return empty updates for non-admin users
      this._pendingUpdates = {};
      this._updatesAvailable = false;
      return {};
    }
  }

  // Private method to actually fetch updates
  async _fetchFirebaseUpdates() {
    console.log('[PRACTICE-DEBUG] _fetchFirebaseUpdates called');
    try {
      const adminDocRef = doc(db, 'admin', 'practiceAdmin');
      const updatesCollection = collection(adminDocRef, 'updates');
      const snapshot = await getDocs(updatesCollection);
      
      if (!snapshot.empty) {
        const updates = snapshot.docs
          .map(doc => ({
            practiceCode: doc.data().practiceCode || doc.data().prac_code,
            name: doc.data().name || doc.data().updatedName,
            originalName: doc.data().originalName,
            status: doc.data().status || 'pending'
          }))
          .filter(update => update.status === 'pending');

        console.log(`[PRACTICE-DEBUG] Found ${updates.length} pending updates`);
        return updates;
      }
      
      console.log('[PRACTICE-DEBUG] No pending updates found');
      return [];
    } catch (error) {
      console.error('[PRACTICE-DEBUG] Error fetching Firebase updates:', error);
      return [];
    }
  }

  /**
   * CRITICAL FIX: Add a direct method to get practice by code from Firestore
   * instead of relying on PracticeDataSyncService
   */
  async getPracticeByCodeDirect(practiceCode) {
    console.log('[PRACTICE-DEBUG] getPracticeByCodeDirect called with practiceCode:', practiceCode);
    if (!practiceCode) return null;
    
    try {
      // First check in our internal cache
      if (this.practiceCache.allPractices && this.practiceCache.allPractices.length > 0) {
        const practice = this.practiceCache.allPractices.find(p => p.prac_code === practiceCode);
        if (practice) {
          console.log(`Found practice ${practiceCode} in cache:`, practice.prac_name);
          return practice;
        }
      }
      
      // Then try to load from JSON
      try {
        const response = await fetch('/practices.json');
        if (response.ok) {
          const practices = await response.json();
          const practice = practices.find(p => p.prac_code === practiceCode);
          if (practice) {
            console.log(`Found practice ${practiceCode} in JSON:`, practice.prac_name);
            return practice;
          }
        }
      } catch (jsonError) {
        console.error('Error loading from JSON:', jsonError);
      }
      
      // Last resort: Check Firestore for updates
      try {
        const updateQuery = query(
          collection(db, 'admin/practiceUpdates'),
          where('practiceCode', '==', practiceCode)
        );
        
        const snapshot = await getDocs(updateQuery);
        if (!snapshot.empty) {
          const updateData = snapshot.docs[0].data();
          console.log(`Found practice update in Firestore for ${practiceCode}:`, updateData);
          
          return {
            prac_code: practiceCode,
            prac_name: updateData.name || updateData.updatedName,
            // Include minimal practice data
            updatedFromFirestore: true
          };
        }
      } catch (firestoreError) {
        console.error('Error checking Firestore for practice:', firestoreError);
      }
      
      return null;
    } catch (error) {
      console.error(`Error getting practice ${practiceCode}:`, error);
      return null;
    }
  }

  /**
   * Enhance practice data with latest Firebase updates
   * This function ensures any practice data includes the latest name updates from Firebase
   * @param {Array|Object} practiceData - Single practice object or array of practices to enhance
   * @returns {Array|Object} Enhanced practice data with latest Firebase updates
   */
  async enhancePracticeData(practiceData) {
    console.log('[PRACTICE-DEBUG] enhancePracticeData called');
    if (!practiceData) return practiceData;
    
    try {
      const now = Date.now();
      const cacheKey = Array.isArray(practiceData) 
        ? practiceData.map(p => p.prac_code).sort().join(',')
        : practiceData.prac_code;

      // Check if we already have a pending promise for this data
      if (this.enhancedPracticesCache.pendingPromises.has(cacheKey)) {
        console.log('[PRACTICE-DEBUG] Using pending enhancement promise');
        return this.enhancedPracticesCache.pendingPromises.get(cacheKey);
      }
      
      // Check cache first
      if (this.enhancedPracticesCache.data.has(cacheKey) &&
          now - this.enhancedPracticesCache.timestamp < this.enhancedPracticesCache.TTL) {
        console.log('[PRACTICE-DEBUG] Using cached enhanced practices');
        return this.enhancedPracticesCache.data.get(cacheKey);
      }

      // Create enhancement promise
      const enhancementPromise = (async () => {
        console.log('[PRACTICE-DEBUG] Enhancing practice data with updates');
        const updates = await this.getPendingUpdates();
        
        if (!updates || updates.length === 0) {
          return practiceData;
        }

        const updateMap = new Map(
          updates.map(update => [update.practiceCode, update])
        );

        const enhancedData = Array.isArray(practiceData)
          ? practiceData.map(practice => this._applyUpdate(practice, updateMap))
          : this._applyUpdate(practiceData, updateMap);

        // Cache the result
        this.enhancedPracticesCache.data.set(cacheKey, enhancedData);
        this.enhancedPracticesCache.timestamp = now;
        
        return enhancedData;
      })();

      // Store the promise in pendingPromises
      this.enhancedPracticesCache.pendingPromises.set(cacheKey, enhancementPromise);
      
      // Wait for the enhancement to complete
      const result = await enhancementPromise;
      
      // Clear the pending promise
      this.enhancedPracticesCache.pendingPromises.delete(cacheKey);
      
      return result;
    } catch (error) {
      console.error('[PRACTICE-DEBUG] Error enhancing practice data:', error);
      return practiceData;
    }
  }

  // Helper to apply a single update
  _applyUpdate(practice, updateMap) {
    console.log('[PRACTICE-DEBUG] _applyUpdate called');
    const update = updateMap.get(practice.prac_code);
    if (update) {
      return { ...practice, prac_name: update.name };
    }
    return practice;
  }

  /**
   * Get all practices with proper Firebase updates applied
   */
  async getAllPractices(forceRefresh = false) {
    console.log('[PRACTICE-DEBUG] getAllPractices called with forceRefresh:', forceRefresh);
    try {
      // If we're already loading, return the existing promise
      if (this.practiceCache.loadPromise) {
        return await this.practiceCache.loadPromise;
      }
      
      // Check if we can use cached data
      if (!forceRefresh && 
          this.practiceCache.allPractices.length > 0 && 
          this.practiceCache.timestamp && 
          Date.now() - this.practiceCache.timestamp < 3600000) { // 1 hour cache
        return this.practiceCache.allPractices;
      }
      
      // Create a new load promise
      this.practiceCache.loadPromise = this._loadPracticesFromSource();
      
      try {
        const practices = await this.practiceCache.loadPromise;
        this.practiceCache.allPractices = practices;
        this.practiceCache.timestamp = Date.now();
        return practices;
      } finally {
        this.practiceCache.loadPromise = null;
      }
    } catch (error) {
      console.error('Error loading practices:', error);
      // Return cached data if available, otherwise empty array
      return this.practiceCache.allPractices || [];
    }
  }

  async _loadPracticesFromSource() {
    console.log('[PRACTICE-DEBUG] _loadPracticesFromSource called');
    try {
      console.log('Loading practices from source...');
      const response = await fetch('/practices.json');
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const practices = await response.json();
      console.log(`Loaded ${practices.length} practices`);
      return practices;
    } catch (error) {
      console.error('Error loading practices from source:', error);
      throw error;
    }
  }

  /**
   * Get practices by specific practice codes
   * @param {Array} practiceCodes - Array of practice codes to retrieve
   * @param {Object} options - Additional options
   * @returns {Array} - Array of practice objects
   */
  async getPracticesByCodes(practiceCodes, options = {}) {
    console.log('[PRACTICE-DEBUG] getPracticesByCodes called with practiceCodes:', practiceCodes);
    if (!practiceCodes || !Array.isArray(practiceCodes) || practiceCodes.length === 0) {
      console.warn('No practice codes provided to getPracticesByCodes');
      return [];
    }

    try {
      // IMPROVED: Add diagnostic logging
      console.log(`Getting ${practiceCodes.length} practices by codes`);

      // First load all practices to ensure we have the full dataset
      const allPractices = await this.getAllPractices();
      
      // Filter practices that match the requested codes
      const matchingPractices = allPractices.filter(practice => 
        practiceCodes.includes(practice.prac_code)
      );
      
      // CRITICAL FIX: If we didn't find all requested practices, log diagnostics
      if (matchingPractices.length < practiceCodes.length) {
        console.warn(`Only found ${matchingPractices.length} of ${practiceCodes.length} requested practices`);
        const missingCodes = practiceCodes.filter(code => 
          !matchingPractices.some(p => p.prac_code === code)
        );
        console.warn('Missing practice codes:', missingCodes);
      }
      
      // Apply any pending Firestore updates if provided
      if (options.pendingUpdates) {
        return this._applyPendingUpdates(matchingPractices, options.pendingUpdates);
      }
      
      return matchingPractices;
    } catch (error) {
      console.error('Error getting practices by codes:', error);
      return [];
    }
  }

  // The rest of your code remains the same...

  /**
   * Initialize practice data
   */
  async initialize(forceRefresh = false) {
    console.log('[PRACTICE-DEBUG] initialize called with forceRefresh:', forceRefresh);
    // If we already have an initialization in progress, return that promise
    if (this.initPromise && !forceRefresh) {
      return this.initPromise;
    }
    
    // Create a new initialization promise
    this.initPromise = this._doInitialize(forceRefresh);
    return this.initPromise;
  }
  
  /**
   * Internal initialization logic
   */
  async _doInitialize(forceRefresh) {
    console.log('[PRACTICE-DEBUG] _doInitialize called with forceRefresh:', forceRefresh);
    try {
      // Check if data is already loaded and not forced to refresh
      if (this.practices && this.lastInitTime && !forceRefresh) {
        const now = Date.now();
        if (now - this.lastInitTime < this.cacheDuration) {
          return this.practices;
        }
      }

      const data = await this._fetchPracticesData(forceRefresh);
      this.practices = data;
      this.lastInitTime = Date.now();
      this.initialized = true;
      return this.practices;
    } catch (error) {
      console.error('Error initializing practice data:', error);
      throw error;
    }
  }

  /**
   * Fetch practices data from source
   */
  async _fetchPracticesData(forceRefresh) {
    console.log('[PRACTICE-DEBUG] _fetchPracticesData called with forceRefresh:', forceRefresh);
    try {
      // Check if we can use cached data
      if (!forceRefresh && this.practiceCache.allPractices.length > 0) {
        return this.practiceCache.allPractices;
      }

      // Fetch from JSON file
      const response = await fetch('/practices.json');
      if (!response.ok) {
        throw new Error(`Failed to load practices: ${response.status}`);
      }
      
      const data = await response.json();
      console.log(`Loaded ${data.length} practices from JSON file`);

      // Apply any Firestore updates
      try {
        const pendingUpdates = await this.getPendingUpdates();
        
        if (pendingUpdates && pendingUpdates.length > 0) {
          console.log(`Applying ${pendingUpdates.length} pending updates to practices`);
          
          const practiceMap = new Map(data.map(practice => [practice.prac_code, practice]));
          
          pendingUpdates.forEach(update => {
            const practice = practiceMap.get(update.practiceCode);
            if (practice) {
              console.log(`Updating ${practice.prac_code} name: ${practice.prac_name} -> ${update.name}`);
              practice.prac_name = update.name;
            }
          });
          
          const updatedPractices = Array.from(practiceMap.values());
          
          // Cache the updated practices
          this.updateLocalCache(updatedPractices, 'json+firebase-updates', true);
          return updatedPractices;
        }
      } catch (updateError) {
        console.error('Error applying Firebase updates:', updateError);
      }

      // Cache and return raw data if no updates applied
      this.updateLocalCache(data, 'json-direct', true);
      return data;
    } catch (error) {
      console.error('Error fetching practice data:', error);
      throw error;
    }
  }
  
  /**
   * Apply pending updates to practice data
   */
  _applyPendingUpdates(practices, pendingUpdates) {
    console.log('[PRACTICE-DEBUG] _applyPendingUpdates called');
    console.log(`Applying ${pendingUpdates.length} pending updates to ${practices.length} practices`);
    
    const practiceMap = new Map();
    practices.forEach(practice => {
      practiceMap.set(practice.prac_code, practice);
    });
    
    pendingUpdates.forEach(update => {
      const practice = practiceMap.get(update.practiceCode);
      if (practice) {
        console.log(`Applied update to ${practice.prac_code}: ${practice.prac_name} -> ${update.name}`);
        practice.prac_name = update.name;
      }
    });
    
    console.log(`Applied ${pendingUpdates.length} updates to practices array`);
    return Array.from(practiceMap.values());
  }
  
  /**
   * Update the local cache with new practice data
   * @param {Array} practices Array of practices to cache
   * @param {string} source Source identifier for debugging
   * @param {boolean} useAllPracticesCache Whether to update the allPractices cache (true) or userSelections cache (false)
   */
  updateLocalCache(practices, source, useAllPracticesCache = false) {
    console.log('[PRACTICE-DEBUG] updateLocalCache called');
    if (!practices || !Array.isArray(practices)) {
      console.error('Invalid practices data provided to updateLocalCache');
      return;
    }
    
    // CRITICAL FIX: Only update the appropriate cache based on the source and size
    if (useAllPracticesCache || practices.length > 1000) {
      console.log(`Updating local cache with ${practices.length} practices (source: ${source})`);
      this.practiceCache.allPractices = practices;
      this.practiceCache.timestamp = new Date();
    } else {
      // For small arrays (user selections), don't contaminate the main cache
      const auth = getAuth();
      if (auth.currentUser?.uid) {
        console.log(`Updating user selections cache with ${practices.length} practices (source: ${source})`);
        this.practiceCache.userSelections[auth.currentUser.uid] = practices;
      } else {
        console.warn('Attempted to update user selections without a logged in user');
      }
    }
  }
  
  /**
   * Get practice by code - use our direct method now
   */
  async getPracticeByCode(practiceCode) {
    console.log('[PRACTICE-DEBUG] getPracticeByCode called with practiceCode:', practiceCode);
    if (!practiceCode) {
      return null;
    }

    try {
      // Initialize data if needed
      await this.initialize();
      
      // Check for updates before using cached data
      if (this._updatesAvailable) {
        const updates = await this.getPendingUpdates();
        if (updates[practiceCode]) {
          this._log(`Update found for ${practiceCode}, clearing cache`);
          this._enhancedPracticeCache.delete(practiceCode);
        }
      }

      // Search in loaded practices
      if (this.practices) {
        const practice = this.practices.find(p => p.prac_code === practiceCode);
        if (practice) {
          // Enhance with Firebase updates before returning
          return (await this.enhancePracticeData(practice));
        }
      }
      
      // If not found, try to find in full dataset
      if (this.practiceCache.allPractices && this.practiceCache.allPractices.length > 0) {
        const practice = this.practiceCache.allPractices.find(p => p.prac_code === practiceCode);
        if (practice) {
          // Enhance with Firebase updates before returning
          return (await this.enhancePracticeData(practice));
        }
      }
      
      // As a last resort, use our direct Firestore method
      return await this.getPracticeByCodeDirect(practiceCode);
    } catch (error) {
      console.error(`Error getting practice ${practiceCode}:`, error);
      return null;
    }
  }

  /**
   * Get user's selected practices
   * @param {string} userId User ID
   * @returns {Array} Array of selected practices
   */
  // Add improved debugging to getUserPractices method
  async getUserPractices(userId) {
    console.log('[PRACTICE-DEBUG] getUserPractices called with userId:', userId);
    if (!userId) return [];
    
    const now = Date.now();

    // Return existing promise if one is in flight for this user
    if (this._practiceLoadLock.promises.has(userId)) {
      console.log(`[PRACTICE-DEBUG] Using existing practice load promise for user ${userId}`);
      return this._practiceLoadLock.promises.get(userId);
    }

    // Create new promise for this user
    const loadPromise = (async () => {
      try {
        // First check cache
        if (this.practiceCache.userSelections[userId]?.length > 0) {
          console.log(`[PRACTICE-DEBUG] Using cached practices for user ${userId}`);
          return this.practiceCache.userSelections[userId];
        }

        // Ensure initialization
        await this.initializePracticeData();

        const savedPracticeCodes = JSON.parse(localStorage.getItem('selectedPractices') || '[]');
        if (savedPracticeCodes.length === 0) return [];

        // Match against loaded practices
        const matchedPractices = this.practices.filter(p => 
          savedPracticeCodes.includes(p.prac_code)
        );

        // Single enhancement call with caching
        const enhancedPractices = await this.enhancePracticeData(matchedPractices);
        
        // Cache the enhanced results
        this.practiceCache.userSelections[userId] = enhancedPractices;
        
        return enhancedPractices;
      } catch (error) {
        console.error('[PRACTICE-DEBUG] Error in getUserPractices:', error);
        return [];
      }
    })();

    // Store the promise
    this._practiceLoadLock.promises.set(userId, loadPromise);

    try {
      const result = await loadPromise;
      return result;
    } finally {
      // Clear the promise after completion
      this._practiceLoadLock.promises.delete(userId);
    }
  }
  
  // FIX the setUserPractices method to handle arrays properly
  setUserPractices(userId, practices) {
    console.log('[PRACTICE-DEBUG] setUserPractices called with userId:', userId, 'and practices:', practices);
    if (!userId) {
      console.error('[PRACTICE-DEBUG] No userId provided to setUserPractices');
      return;
    }
    
    if (!practices) {
      console.error('[PRACTICE-DEBUG] No practices provided to setUserPractices');
      return;
    }
    
    // IMPROVED: Make a deep copy to prevent shared references
    let practicesCopy;
    
    // IMPROVED: Don't convert single objects to arrays
    if (!Array.isArray(practices)) {
      console.error('[PRACTICE-DEBUG] Practices must be an array, got:', typeof practices);
      return;
    } else {
      // Make a deep copy of each practice object
      practicesCopy = practices.map(practice => ({...practice}));
    }
    
    console.log(`[PRACTICE-DEBUG] Setting ${practicesCopy.length} user practices for ${userId}`);
    
    // Log the first practice to see its structure
    if (practicesCopy.length > 0) {
      console.log('[PRACTICE-DEBUG] First practice:', {
        prac_code: practicesCopy[0].prac_code,
        prac_name: practicesCopy[0].prac_name,
        keys: Object.keys(practicesCopy[0])
      });
    }
    
    // CRITICAL FIX: Make sure we have the user's subsection
    if (!this.practiceCache.userSelections) {
      this.practiceCache.userSelections = {};
    }
    
    this.practiceCache.userSelections[userId] = practicesCopy;
    
    // IMPROVED: Save to localStorage for better persistence
    try {
      const practiceCodes = practicesCopy.map(p => p.prac_code);
      localStorage.setItem('selectedPractices', JSON.stringify(practiceCodes));
      console.log('[PRACTICE-DEBUG] Saved practice codes to localStorage:', practiceCodes.join(', '));
    } catch (error) {
      console.error('[PRACTICE-DEBUG] Failed to save practice codes to localStorage:', error);
    }
  }
  
  /**
   * Clear the cache completely or for a specific user
   * @param {string} userId Optional user ID to clear only that user's cache
   */
  clearCache(userId = null) {
    console.log('[PRACTICE-DEBUG] clearCache called with userId:', userId);
    if (userId) {
      console.log(`Clearing cache for user ${userId}`);
      delete this.practiceCache.userSelections[userId];
    } else {
      console.log('Clearing all caches');
      this.practiceCache.allPractices = [];
      this.practiceCache.userSelections = {};
      this.practiceCache.timestamp = null;
    }
  }
  
  /**
   * Search practices by name or code
   */
  searchPractices(searchTerm, limit = 10) {
    console.log('[PRACTICE-DEBUG] searchPractices called with searchTerm:', searchTerm, 'and limit:', limit);
    if (!searchTerm || typeof searchTerm !== 'string' || searchTerm.length < 3) {
      return [];
    }
    
    const term = searchTerm.toLowerCase().trim();
    
    // Use appropriate dataset based on availability
    const dataToSearch = this.practiceCache.allPractices.length > 1000 
      ? this.practiceCache.allPractices 
      : this.practices;
      
    if (!dataToSearch || !dataToSearch.length) {
      return [];
    }
    
    // Perform the search
    return dataToSearch
      .filter(practice => {
        const name = (practice.prac_name || '').toLowerCase();
        const code = (practice.prac_code || '').toLowerCase();
        const address1 = (practice.address1 || '').toLowerCase();
        const address4 = (practice.address4 || '').toLowerCase();
        const address5 = (practice.address5 || '').toLowerCase();
        const town = (practice.town || '').toLowerCase();
        const postcode = (practice.postcode || '').toLowerCase();
        
        return name.includes(term) || 
               code.includes(term) || 
               address1.includes(term) || 
               address4.includes(term) || 
               address5.includes(term) || 
               town.includes(term) || 
               postcode.includes(term);
      })
      .slice(0, limit);
  }
  
  /**
   * Check for updated practice data
   */
  async checkForUpdates() {
    console.log('[PRACTICE-DEBUG] checkForUpdates called');
    const now = Date.now();
    // Only check once per hour
    if (now - this.lastVersionCheck < 60 * 60 * 1000) {
      return false;
    }
    
    this.lastVersionCheck = now;
    try {
      const hasUpdates = await this.getPendingUpdates();
      return hasUpdates;
    } catch (error) {
      console.error('Error checking for updates:', error);
      return false;
    }
  }

  /**
   * New combined method to initialize and get user practices in one call
   */
  async getUserPracticesInitialized(userId) {
    console.log('[PRACTICE-DEBUG] getUserPracticesInitialized called with userId:', userId);
    if (!userId) return [];
    
    // Use existing initialization lock
    if (this._globalInitLock.promise) {
      console.log('[PRACTICE-DEBUG] Waiting for existing initialization');
      await this._globalInitLock.promise;
    }
    
    // Use existing practice load promise if available
    if (this._practiceLoadLock.promises.has(userId)) {
      console.log('[PRACTICE-DEBUG] Using existing practice load promise');
      return this._practiceLoadLock.promises.get(userId);
    }

    // Create new promise for combined operation
    const loadPromise = (async () => {
      try {
        // First ensure initialization
        if (!this.isInitialized()) {
          console.log('[PRACTICE-DEBUG] Performing initialization');
          await this.initializePracticeData();
        }

        // Then load user practices
        console.log('[PRACTICE-DEBUG] Loading user practices');
        const practices = await this._loadUserPractices(userId);
        
        // Cache the result
        this.practiceCache.userSelections[userId] = practices;
        
        return practices;
      } catch (error) {
        console.error('[PRACTICE-DEBUG] Error in getUserPracticesInitialized:', error);
        return [];
      }
    })();

    // Store the promise
    this._practiceLoadLock.promises.set(userId, loadPromise);

    try {
      return await loadPromise;
    } finally {
      // Clear the promise
      this._practiceLoadLock.promises.delete(userId);
    }
  }

  // New private method to load user practices
  async _loadUserPractices(userId) {
    console.log('[PRACTICE-DEBUG] _loadUserPractices called with userId:', userId);
    // Check cache first
    if (this.practiceCache.userSelections[userId]?.length > 0) {
      console.log('[PRACTICE-DEBUG] Using cached user practices');
      return this.practiceCache.userSelections[userId];
    }

    const savedPracticeCodes = JSON.parse(localStorage.getItem('selectedPractices') || '[]');
    if (savedPracticeCodes.length === 0) return [];

    // Match against loaded practices
    const matchedPractices = this.practices.filter(p => 
      savedPracticeCodes.includes(p.prac_code)
    );

    // Single enhancement call
    return this.enhancePracticeData(matchedPractices);
  }

  async initialize(forceRefresh = false) {
    // Don't re-initialize if already done
    if (this._initialized && !forceRefresh) {
      this._log('Already initialized, skipping');
      return;
    }

    // Prevent concurrent initialization
    if (this._initializing) {
      this._log('Initialization in progress, waiting...');
      return this._initializePromise;
    }

    try {
      this._initializing = true;
      this._initializePromise = this._doInitialize(forceRefresh);
      await this._initializePromise;
    } catch (error) {
      this._error('Initialization failed:', error);
      throw error;
    } finally {
      this._initializing = false;
    }
  }

  async _doInitialize(forceRefresh) {
    try {
      // Try localStorage first
      if (!forceRefresh) {
        const cached = this._getFromLocalStorage();
        if (cached) {
          this._practiceMap = new Map(Object.entries(cached));
          this._initialized = true;
          this._lastInitialized = Date.now();
          this._log('Loaded from localStorage');
          return;
        }
      }

      // Fetch from JSON file
      const response = await fetch('/practices.json');
      if (!response.ok) throw new Error('Failed to fetch practices');
      
      const practices = await response.json();
      
      // Build practice map
      this._practiceMap.clear();
      practices.forEach(practice => {
        if (practice.prac_code) {
          this._practiceMap.set(practice.prac_code, practice);
        }
      });

      // Update localStorage
      this._saveToLocalStorage();
      
      this._initialized = true;
      this._lastInitialized = Date.now();
      this._log(`Initialized with ${practices.length} practices`);
    } catch (error) {
      this._error('Initialization error:', error);
      throw error;
    }
  }

  _getFromLocalStorage() {
    try {
      const cached = localStorage.getItem('practices_data');
      const timestamp = localStorage.getItem('practices_timestamp');
      
      if (cached && timestamp) {
        const age = Date.now() - parseInt(timestamp);
        if (age < 24 * 60 * 60 * 1000) { // 24 hours
          return JSON.parse(cached);
        }
      }
      return null;
    } catch (error) {
      this._error('LocalStorage error:', error);
      return null;
    }
  }

  _saveToLocalStorage() {
    try {
      const data = Object.fromEntries(this._practiceMap);
      localStorage.setItem('practices_data', JSON.stringify(data));
      localStorage.setItem('practices_timestamp', Date.now().toString());
    } catch (error) {
      this._error('Failed to cache in localStorage:', error);
    }
  }

  async getPracticeByCode(code) {
    if (!code) return null;

    // Check enhanced cache first
    if (this._enhancedPracticeCache.has(code)) {
      this._log(`Cache hit for practice: ${code}`);
      return this._enhancedPracticeCache.get(code);
    }

    // Initialize if needed
    if (!this._initialized) {
      await this.initialize();
    }

    // Get base practice data
    const practice = this._practiceMap.get(code);
    if (!practice) {
      this._log(`Practice not found: ${code}`);
      return null;
    }

    // Enhance with updates and cache
    const enhanced = await this._enhancePractice(practice);
    this._enhancedPracticeCache.set(code, enhanced);
    
    return enhanced;
  }

  async _enhancePractice(practice) {
    if (!practice) return null;

    // Get updates if needed
    const updates = await this._getPendingUpdates();
    if (updates?.[practice.prac_code]) {
      this._log(`Applying updates for practice: ${practice.prac_code}`);
      return { ...practice, ...updates[practice.prac_code] };
    }

    return practice;
  }

  async _getPendingUpdates(forceRefresh = false) {
    // Use cached updates if available
    if (this._pendingUpdates && !forceRefresh) {
      this._log('Using cached updates');
      return this._pendingUpdates;
    }

    // Throttle update checks (once per hour)
    if (!forceRefresh && this._lastUpdateCheck) {
      const age = Date.now() - this._lastUpdateCheck;
      if (age < 60 * 60 * 1000) {
        this._log('Update check throttled');
        return this._pendingUpdates || {};
      }
    }

    try {
      this._lastUpdateCheck = Date.now();
      
      this._log('Getting practice updates from admin/practiceupdates document');
      
      // Get the single document at admin/practiceupdates
      const practiceUpdatesDocRef = doc(db, 'admin', 'practiceUpdates');
      const practiceUpdatesDoc = await getDoc(practiceUpdatesDocRef);
      
      const updates = {};
      
      if (practiceUpdatesDoc.exists()) {
        this._log('Found practice updates document');
        
        // Get the updatedPractice field which contains the map of updates
        const data = practiceUpdatesDoc.data();
        
        if (data && data.updatedPractice) {
          this._log('Found updatedPractice map with practice updates');
          
          // Extract practice code from the updatedPractice map
          const pracCode = data.updatedPractice.prac_code;
          
          if (pracCode) {
            // Store the entire updatedPractice object under the practice code key
            updates[pracCode] = data.updatedPractice;
            
            this._log(`Found update for practice: ${pracCode} (${data.updatedPractice.prac_name})`);
            this._enhancedPracticeCache.delete(pracCode);
            
            // DIRECT UPDATE: If we have this practice in the practice map, update it immediately
            if (this._practiceMap.has(pracCode)) {
              const existingPractice = this._practiceMap.get(pracCode);
              const updatedPractice = {...existingPractice, ...data.updatedPractice};
              this._practiceMap.set(pracCode, updatedPractice);
              this._log(`Directly updated practice ${pracCode} in cache`);
              
              // Set update timestamp to notify components data has changed
              this._lastUpdateTimestamp = Date.now();
              this._log(`✅ Updated timestamp to ${this._lastUpdateTimestamp}`);
              
              // ENHANCED LOGGING: Update localStorage cache with the updated practice data
              try {
                // Get the current localStorage cache
                this._log('🔄 Attempting to update localStorage practice data cache...');
                const cachedData = localStorage.getItem('practices_data');
                
                if (cachedData) {
                  this._log('✅ Found existing practices_data in localStorage');
                  let practices = [];
                  
                  try {
                    practices = JSON.parse(cachedData);
                    this._log(`📊 localStorage has ${practices.length} practices`);
                  } catch (parseError) {
                    this._error('❌ JSON parse error for localStorage data:', parseError);
                    throw parseError;
                  }
                  
                  let hasChanges = false;
                  let practiceFound = false;
                  
                  // Find and update the practice in the localStorage cache
                  for (let i = 0; i < practices.length; i++) {
                    if (practices[i].prac_code === pracCode) {
                      this._log(`✅ Found practice ${pracCode} at index ${i} in localStorage`);
                      practiceFound = true;
                      
                      // Log the before state
                      this._log('📝 BEFORE update:', JSON.stringify(practices[i]));
                      
                      // Apply updates to the practice object
                      Object.assign(practices[i], data.updatedPractice);
                      
                      // Log the after state
                      this._log('📝 AFTER update:', JSON.stringify(practices[i]));
                      
                      hasChanges = true;
                      break;
                    }
                  }
                  
                  // If practice was not found, log this
                  if (!practiceFound) {
                    this._error(`❌ Practice ${pracCode} not found in localStorage cache!`);
                  }
                  
                  // If practice was updated, save back to localStorage
                  if (hasChanges) {
                    this._log('💾 Saving updated practices to localStorage...');
                    
                    try {
                      const jsonData = JSON.stringify(practices);
                      localStorage.setItem('practices_data', jsonData);
                      localStorage.setItem('practices_timestamp', Date.now().toString());
                      this._log(`✅ Successfully saved ${practices.length} practices to localStorage`);
                      this._log(`📝 localStorage update timestamp: ${Date.now()}`);
                    } catch (storageError) {
                      this._error('❌ Failed to write to localStorage:', storageError);
                      throw storageError;
                    }
                  } else {
                    this._log('ℹ️ No changes to save to localStorage');
                  }
                } else {
                  this._log('❌ No practices_data found in localStorage');
                }
              } catch (storageError) {
                this._error('❌ Failed to update localStorage cache:', storageError);
              }
            } else {
              this._log(`❌ Practice ${pracCode} not found in in-memory practice map`);
            }
          }
        } else {
          this._log('No updatedPractice map found in the document');
        }
      } else {
        this._log('admin/practiceupdates document not found');
      }
      
      this._pendingUpdates = updates;
      this._log(`Processed ${Object.keys(updates).length} practice updates`);
      
      this._updatesAvailable = true;
      
      return updates;
    } catch (error) {
      this._error('Error getting practice updates:', error);
      return {};
    }
  }

  _log(...args) {
    if (this.debug) {
      console.log('[PRACTICE-DEBUG]', ...args);
    }
  }

  _error(...args) {
    console.error('[PRACTICE-ERROR]', ...args);
  }

  /**
   * Check if updates are available without applying them
   * @param {boolean} forceRefresh - Force a fresh check
   * @returns {Promise<{available: boolean, count: number}>}
   */
  async checkForUpdatesAvailable(forceRefresh = false) {
    try {
      const updates = await this.getPendingUpdates(forceRefresh);
      const updateCount = Object.keys(updates).length;
      
      this._updatesAvailable = updateCount > 0;
      this._log(`Checked for updates: ${updateCount} updates available`);
      
      return {
        available: this._updatesAvailable,
        count: updateCount
      };
    } catch (error) {
      this._error('Error checking for updates:', error);
      return { available: false, error };
    }
  }
  
  /**
   * Returns whether updates are available based on the last check
   */
  hasUpdatesAvailable() {
    return this._updatesAvailable;
  }

  /**
   * Get the timestamp of the last update - components can check this to know when to refresh
   */
  getLastUpdateTimestamp() {
    return this._lastUpdateTimestamp || 0;
  }

  async _updatePracticeData(updatedPractice) {
    const pracCode = updatedPractice.prac_code;
    
    try {
      // 1. Update in-memory cache
      this._enhancedPracticeCache.delete(pracCode);
      if (this._practiceMap.has(pracCode)) {
        const existingPractice = this._practiceMap.get(pracCode);
        const mergedPractice = {...existingPractice, ...updatedPractice};
        this._practiceMap.set(pracCode, mergedPractice);
      }

      // 2. Update localStorage
      const cachedData = localStorage.getItem('practices_data');
      if (cachedData) {
        const practices = JSON.parse(cachedData);
        const practiceIndex = practices.findIndex(p => p.prac_code === pracCode);
        
        if (practiceIndex >= 0) {
          practices[practiceIndex] = {
            ...practices[practiceIndex],
            ...updatedPractice
          };
          
          localStorage.setItem('practices_data', JSON.stringify(practices));
          localStorage.setItem('practices_timestamp', Date.now().toString());
          this._log(`Updated practice ${pracCode} in localStorage`);
        }
      }

      // 3. Update timestamp to trigger UI refresh
      this._lastUpdateTimestamp = Date.now();
      
    } catch (error) {
      this._error('Error updating practice data:', error);
      throw error;
    }
  }
}

// Create a single instance of the practiceDataUtil with all functionality merged
export const practiceDataUtil = new PracticeDataUtil();

// Add an explicit initialization method that should be called after login
export const initializePracticeData = async (user) => {
  if (!user || !user.uid) {
    console.log('[PRACTICE-DEBUG] No authenticated user, skipping practice data initialization');
    return;
  }
  
  console.log(`[PRACTICE-DEBUG] Initializing practice data for user: ${user.uid}`);
  try {
    await practiceDataUtil.checkForUpdates();
    console.log('[PRACTICE-DEBUG] Practice data initialization complete');
  } catch (e) {
    console.warn('[PRACTICE-DEBUG] Failed to initialize practices cache:', e);
  }
};

// Replace the self-invoking function to not automatically load on module import
// This prevents the Firestore queries before authentication
(function() {
  // Simply prepare the utility but don't fetch data
  console.log('[PRACTICE-DEBUG] Practice data module loaded - waiting for authentication');
})();